import { ImportTableDefinition } from '../importTableDefinition';
import type { ImportTableDefinitionRevision } from './index';

import SchemaJson = ImportTableDefinition.SchemaJson;

/**
 * 旧 revision の内容と新しい候補内容を比較して、後方互換性を判定する純粋関数。
 *
 * 判定方針は plan を参照:
 * - 任意列の追加、説明文変更は `compatible`
 * - 列削除、列名変更、型変更、`destination` 変更、`required: false -> true`、
 *   `uniqueKeys` 変更、`loadMode` 変更は `possiblyBreaking`
 *
 * 比較対象が完全に同一なら `compatible` かつ差分なし（呼び出し側は差分なしを
 * 「新 revision 不要」として扱う）。
 */
export type CompatibilityCheckInput = {
  schemaJson: SchemaJson;
  uniqueKeys: ImportTableDefinition.UniqueKeys;
  loadMode: ImportTableDefinition.LoadMode;
  インポート経路: ImportTableDefinition.インポート経路;
};

export type CompatibilityResult = {
  compatibilityType: 'compatible' | 'possiblyBreaking';
  hasDiff: boolean;
  changeSummary: ImportTableDefinitionRevision.ChangeSummary;
  breakingReasons: string[];
};

export const toCompatibilityCheckInput = (rev: ImportTableDefinitionRevision): CompatibilityCheckInput => ({
  schemaJson: rev.schemaJson,
  uniqueKeys: rev.uniqueKeys,
  loadMode: rev.loadMode,
  インポート経路: rev.インポート経路,
});

const sameArray = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

const compareColumns = (
  base: SchemaJson.Column[],
  next: SchemaJson.Column[],
): {
  addedColumns: string[];
  removedColumns: string[];
  changedColumns: { columnName: string; reasons: string[] }[];
  breakingChangedColumns: { columnName: string; reasons: string[] }[];
} => {
  const baseByName = new Map(base.map((c) => [c.name, c]));
  const nextByName = new Map(next.map((c) => [c.name, c]));

  const addedColumns: string[] = [];
  const removedColumns: string[] = [];
  const changedColumns: { columnName: string; reasons: string[] }[] = [];
  const breakingChangedColumns: { columnName: string; reasons: string[] }[] = [];

  for (const name of nextByName.keys()) {
    if (!baseByName.has(name)) addedColumns.push(name);
  }
  for (const name of baseByName.keys()) {
    if (!nextByName.has(name)) removedColumns.push(name);
  }
  for (const [name, baseCol] of baseByName.entries()) {
    const nextCol = nextByName.get(name);
    if (!nextCol) continue;
    const reasons: string[] = [];
    const breakingReasons: string[] = [];
    if (baseCol.type !== nextCol.type) reasons.push(`type ${baseCol.type} -> ${nextCol.type}`);
    if (baseCol.type !== nextCol.type) breakingReasons.push(`type ${baseCol.type} -> ${nextCol.type}`);
    if (baseCol.destination !== nextCol.destination) {
      reasons.push(`destination ${baseCol.destination} -> ${nextCol.destination}`);
      breakingReasons.push(`destination ${baseCol.destination} -> ${nextCol.destination}`);
    }
    if (!baseCol.required && nextCol.required) {
      reasons.push('required false -> true');
      breakingReasons.push('required false -> true');
    }
    if ((baseCol.multiValued ?? false) !== (nextCol.multiValued ?? false)) {
      const from = baseCol.multiValued === true;
      const to = nextCol.multiValued === true;
      reasons.push(`multiValued ${from} -> ${to}`);
      breakingReasons.push(`multiValued ${from} -> ${to}`);
    }
    if (baseCol.description !== nextCol.description) reasons.push('description changed');
    if (reasons.length > 0) changedColumns.push({ columnName: name, reasons });
    if (breakingReasons.length > 0) breakingChangedColumns.push({ columnName: name, reasons: breakingReasons });
  }

  return { addedColumns, removedColumns, changedColumns, breakingChangedColumns };
};

const isAddedColumnRequired = (next: SchemaJson.Column[], names: string[]): string[] => {
  return names.filter((n) => next.find((c) => c.name === n)?.required === true);
};

export const checkCompatibility = (baseInput: CompatibilityCheckInput, next: CompatibilityCheckInput): CompatibilityResult => {
  const { addedColumns, removedColumns, changedColumns, breakingChangedColumns } = compareColumns(
    baseInput.schemaJson.columns,
    next.schemaJson.columns,
  );

  const breakingReasons: string[] = [];
  const otherChanges: string[] = [];

  if (removedColumns.length > 0) breakingReasons.push(`removed columns: ${removedColumns.join(', ')}`);
  if (breakingChangedColumns.length > 0) {
    breakingReasons.push(`changed columns: ${breakingChangedColumns.map((c) => `${c.columnName}(${c.reasons.join(', ')})`).join('; ')}`);
  }
  const requiredAdded = isAddedColumnRequired(next.schemaJson.columns, addedColumns);
  if (requiredAdded.length > 0) {
    breakingReasons.push(`required columns added: ${requiredAdded.join(', ')}`);
  }

  if (baseInput.loadMode !== next.loadMode) {
    breakingReasons.push(`loadMode ${baseInput.loadMode} -> ${next.loadMode}`);
  }
  if (baseInput.インポート経路 !== next.インポート経路) {
    breakingReasons.push(`importMethod ${baseInput.インポート経路} -> ${next.インポート経路}`);
  }
  if (baseInput.schemaJson.encoding !== next.schemaJson.encoding) {
    breakingReasons.push(`encoding ${baseInput.schemaJson.encoding} -> ${next.schemaJson.encoding}`);
  }
  if (baseInput.schemaJson.delimiter !== next.schemaJson.delimiter) {
    breakingReasons.push(`delimiter ${baseInput.schemaJson.delimiter} -> ${next.schemaJson.delimiter}`);
  }
  if (baseInput.schemaJson.hasHeaderRow !== next.schemaJson.hasHeaderRow) {
    breakingReasons.push(`hasHeaderRow ${baseInput.schemaJson.hasHeaderRow} -> ${next.schemaJson.hasHeaderRow}`);
  }

  if (!sameArray(baseInput.uniqueKeys.contract.columnNames, next.uniqueKeys.contract.columnNames)) {
    breakingReasons.push('contract uniqueKeys changed');
  }
  const baseCustomer = baseInput.uniqueKeys.customer;
  const nextCustomer = next.uniqueKeys.customer;
  if ((baseCustomer && !nextCustomer) || (!baseCustomer && nextCustomer)) {
    breakingReasons.push('customer uniqueKeys presence changed');
  } else if (baseCustomer && nextCustomer) {
    if (!sameArray(baseCustomer.columnNames, nextCustomer.columnNames) || baseCustomer.identifierType !== nextCustomer.identifierType) {
      breakingReasons.push('customer uniqueKeys changed');
    }
  }

  const hasDiff =
    addedColumns.length > 0 ||
    removedColumns.length > 0 ||
    changedColumns.length > 0 ||
    breakingReasons.length > 0 ||
    otherChanges.length > 0;

  const changeSummary: ImportTableDefinitionRevision.ChangeSummary = {
    addedColumns: addedColumns.length > 0 ? addedColumns : undefined,
    removedColumns: removedColumns.length > 0 ? removedColumns : undefined,
    changedColumns: changedColumns.length > 0 ? changedColumns : undefined,
    otherChanges: otherChanges.length > 0 ? otherChanges : undefined,
  };

  return {
    compatibilityType: breakingReasons.length > 0 ? 'possiblyBreaking' : 'compatible',
    hasDiff,
    changeSummary,
    breakingReasons,
  };
};
